import type {
  AloftDate,
  SimilarityFeature,
  SimilarityScoreBreakdown,
  SimilarityScoreCharacteristics,
} from '@aloft/toolkit-shared';
import { schSimilarityFeatures } from '@aloft/toolkit-shared';
import { Button, Flex, Grid, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { PopoverBody } from '@aloft/toolkit-sui/chakra';

import type { ReactNode } from 'react';
import { useState } from 'react';

import { HoverPopover } from '../../../shared/HoverPopover.tsx';
import { FEATURE_LABELS } from '../../utils/featureLabels.ts';
import { formatFeatureDifference } from '../../utils/formatFeatureDifference.ts';

type Props = {
  breakdown: SimilarityScoreBreakdown;
  subjectCharacteristics: SimilarityScoreCharacteristics;
  compCharacteristics: SimilarityScoreCharacteristics;
  compSaleDate?: AloftDate;
  children: ReactNode;
};

const ALL_FEATURES = schSimilarityFeatures.options as SimilarityFeature[];

export const SimilarityScoreBreakdownPopover = ({
  breakdown,
  subjectCharacteristics,
  compCharacteristics,
  compSaleDate,
  children,
}: Props) => {
  const [showMatches, setShowMatches] = useState(false);

  const scoredFeatures = ALL_FEATURES.filter(
    (f) => breakdown[f] !== undefined && !breakdown[f]!.excluded,
  );
  const excludedFeatures = ALL_FEATURES.filter((f) => breakdown[f]?.excluded === true);
  const matchingFeatures = scoredFeatures.filter((f) => breakdown[f]!.gsc === 1);
  const nonMatchingFeatures = scoredFeatures.filter((f) => breakdown[f]!.gsc < 1);

  const visibleFeatures = showMatches ? scoredFeatures : nonMatchingFeatures;

  return (
    <HoverPopover
      renderContent={({ suppressCloseBriefly }) => {
        const toggleShowMatches = (e: React.MouseEvent) => {
          e.stopPropagation();
          // Suppress the close that fires when the popover shrinks and the cursor briefly lands outside the (now smaller) content box.
          suppressCloseBriefly();
          setShowMatches((prev) => !prev);
        };

        return (
          <PopoverBody padding={0}>
            <Grid
              gridTemplateColumns="1fr 1fr 0.5fr"
              gap="8px"
              padding="8px 16px"
              __dangerDoNotUseSx={{
                textTransform: 'uppercase',
              }}
            >
              <Typography variant="subtitle1" color="textTertiary">
                Feature
              </Typography>
              <Typography variant="subtitle1" color="textTertiary">
                Difference
              </Typography>
              <Typography variant="subtitle1" color="textTertiary" textAlign="right">
                Pts.
              </Typography>
            </Grid>

            {/* Feature rows */}
            <Flex flexDirection="column" gap="8px" padding="8px 16px">
              {visibleFeatures.map((feature) => {
                const item = breakdown[feature]!;
                const isMatch = item.gsc === 1;
                const diff = formatFeatureDifference(
                  feature,
                  subjectCharacteristics,
                  compCharacteristics,
                  compSaleDate,
                );

                return (
                  <Grid key={feature} gridTemplateColumns="1fr 1fr 0.5fr" gap="8px">
                    <Typography variant="h5" color="textPrimary">
                      {FEATURE_LABELS[feature]}
                    </Typography>
                    <Typography variant="body2" color={isMatch ? 'textDisabled' : 'textPrimary'}>
                      {isMatch ? '-' : (diff ?? '-')}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color={isMatch ? 'textSecondary' : 'textError'}
                      textAlign="right"
                    >
                      {isMatch ? '0' : `-${item.points}`}
                    </Typography>
                  </Grid>
                );
              })}
              {showMatches &&
                excludedFeatures.map((feature) => (
                  <Grid key={feature} gridTemplateColumns="1fr 1fr 0.5fr" gap="8px">
                    <Typography variant="h5" color="textDisabled">
                      {FEATURE_LABELS[feature]}
                    </Typography>
                    <Typography variant="body2" color="textDisabled">
                      N/A
                    </Typography>
                    <Typography variant="subtitle1" color="textDisabled" textAlign="right">
                      -
                    </Typography>
                  </Grid>
                ))}
            </Flex>

            <Flex alignItems="center" gap="16px" padding="8px 16px">
              <Typography
                variant="body2"
                color="textTertiary"
                textAlign="right"
                __dangerDoNotUseSx={{ flex: '1 0 0' }}
              >
                {matchingFeatures.length} matching feature{matchingFeatures.length !== 1 ? 's' : ''}
                {excludedFeatures.length > 0 ? `, ${excludedFeatures.length} N/A` : ''}
              </Typography>
              <Button
                variant="inline"
                onClick={toggleShowMatches}
                text={showMatches ? 'Hide matches' : 'Show all'}
              />
            </Flex>
          </PopoverBody>
        );
      }}
    >
      {children}
    </HoverPopover>
  );
};
